import { CompositionSettings } from '@src/components/sidepanel/CompositionSettings';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { useCompositionContext } from '../../sidepanel/CompositionContext';

import type { CompositionTemplate } from '@src/components/sidepanel/CompositionSettings/types';

interface QuoteCardTemplatesPanelProps {
  onAddComposition: (composition: CompositionTemplate) => void;
  activeTemplateId?: string;
}

export const QuoteCardTemplatesPanel = (
  props: QuoteCardTemplatesPanelProps,
) => {
  const userIdentity = useUserIdentity();
  const { contentStudioQuotesEnableGlobalTemplates } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const enableGlobalTemplates =
    contentStudioQuotesEnableGlobalTemplates ?? true;

  const {
    categories,
    setSelectedCategoryId,
    setSelectedSubcategoryId,
    selectedCategoryId,
  } = useCompositionContext();
  const { onAddComposition, activeTemplateId } = props;

  // Filter out global preset compositions from categories when the flag is off
  const filteredCategories = useMemo(() => {
    if (enableGlobalTemplates) return categories;
    return categories
      .map(category => ({
        ...category,
        subcategories: category.subcategories
          .map(subcategory => ({
            ...subcategory,
            compositions: subcategory.compositions.filter(
              comp => !comp.isGlobalPreset,
            ),
          }))
          .filter(subcategory => subcategory.compositions.length > 0),
      }))
      .filter(category => category.subcategories.length > 0);
  }, [categories, enableGlobalTemplates]);

  useEffect(() => {
    // Guard against empty categories array
    if (!filteredCategories || filteredCategories.length === 0) {
      return;
    }
    setSelectedCategoryId(filteredCategories[0].id);
    if (filteredCategories[0].subcategories.length === 1) {
      setSelectedSubcategoryId(filteredCategories[0].subcategories[0].id);
    } else {
      setSelectedSubcategoryId(null);
    }
  }, [filteredCategories, setSelectedCategoryId, setSelectedSubcategoryId]);

  return (
    <Container data-testid="quote-templates-panel">
      {filteredCategories.length > 1 && (
        <Cards>
          {filteredCategories.map(category => (
            <Card
              key={category.id}
              data-testid={`quote-template-${category.id}`}
              isSelected={selectedCategoryId === category.id}
              onClick={() => {
                setSelectedCategoryId(category.id);
                if (category.subcategories.length === 1) {
                  setSelectedSubcategoryId(category.subcategories[0].id);
                } else {
                  setSelectedSubcategoryId(null);
                }
              }}
            >
              {category.label}
            </Card>
          ))}
        </Cards>
      )}
      <CompositionSettings
        activeElement={null}
        onAddComposition={onAddComposition}
        isQuoteCard={true}
        activeTemplateId={activeTemplateId}
        hideGlobalPresets={!enableGlobalTemplates}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`;

const Cards = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-self: start;
  white-space: nowrap;
`;

const Card = styled.div<{ isSelected?: boolean }>`
  border: 1px solid
    ${props => (props.isSelected ? 'var(--accent)' : 'var(--border)')};
  border-radius: 4px;
  font-size: 12px;
  padding: 8px 10px;
  text-align: center;
  color: ${props =>
    props.isSelected ? 'var(--background)' : 'var(--text-muted)'};
  background-color: ${props => (props.isSelected ? 'var(--accent)' : '')};
  cursor: pointer;
  ${props =>
    !props.isSelected &&
    css`
      &:hover {
        color: var(--accent);
        border-color: var(--accent);
      }
    `}
`;
